import React, { useMemo, useState } from 'react';
import { Trash2, Search, ChevronLeft, ChevronRight, Check, Edit3, Filter } from 'lucide-react';
import { User } from '../../types/user';
import Dropdown from '../Dropdown';
import { useAdmin } from '../../contexts/adminContext';

interface Props {
  users: User[];
  onDeleteSelected: (ids: string[]) => void;
  onEdit: (user: User) => void;
  darkMode?: boolean;
}

const pageSizeOptions = [5, 10, 20, 50];

const UserTable: React.FC<Props> = ({ onDeleteSelected, onEdit, darkMode = false }) => {
  const { allUsers } = useAdmin();
  
  // State management
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<('Admin' | 'User')[]>(['Admin', 'User']);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://vela-back.wingtrill.com';

  // Map API data to User interface
  const mappedUsers: User[] = useMemo(() => {
    if (!allUsers || !Array.isArray(allUsers)) return [];
    
    return allUsers.map(apiUser => {
      const nameParts = (apiUser.Name || '').trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      return {
        id: String(apiUser.id),
        firstName,
        lastName,
        email: apiUser.Email || '',
        phoneNumber: apiUser.phone_number || undefined,
        // phoneNumber: '-',
        

        department: apiUser.Department || undefined,
        position: apiUser.job_title || undefined,
        location: apiUser.location || undefined,
        // location: '-',

        role: apiUser.isAdmin ? 'Admin' : 'User',
        createdAt: apiUser.Created_at || new Date().toISOString(),
        // lastLoginAt: apiUser.last_login_at || undefined,
        lastLoginAt: apiUser.last_login_at,
        // avatarUrl: apiUser.avatar_url || undefined,
        avatarUrl: apiUser.avatarUrl != null ? backendUrl + apiUser.avatarUrl : 'https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png',
      } as User;
    });
  }, [allUsers, backendUrl]);

  // Extract unique departments and locations
  const availableDepartments = useMemo(() => {
    const depts = new Set<string>();
    mappedUsers.forEach(u => {
      if (u.department) depts.add(u.department);
    });
    return Array.from(depts).sort();
  }, [mappedUsers]);

  const availableLocations = useMemo(() => {
    const locs = new Set<string>();
    mappedUsers.forEach(u => {
      if (u.location) locs.add(u.location);
    });
    return Array.from(locs).sort();
  }, [mappedUsers]);

  // Filter users based on search and filters
  const filteredUsers = useMemo(() => {
    return mappedUsers.filter(user => {
      // Search filter
      const searchLower = search.toLowerCase();
      const matchesSearch = !search || 
        user.firstName.toLowerCase().includes(searchLower) ||
        user.lastName.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        (user.department && user.department.toLowerCase().includes(searchLower)) ||
        (user.position && user.position.toLowerCase().includes(searchLower)) ||
        (user.location && user.location.toLowerCase().includes(searchLower));

      // Role filter
      const matchesRole = selectedRoles.includes(user.role as 'Admin' | 'User');

      // Department filter
      const matchesDepartment = selectedDepartments.length === 0 || 
        (user.department && selectedDepartments.includes(user.department));

      // Location filter
      const matchesLocation = selectedLocations.length === 0 || 
        (user.location && selectedLocations.includes(user.location));

      return matchesSearch && matchesRole && matchesDepartment && matchesLocation;
    });
  }, [mappedUsers, search, selectedRoles, selectedDepartments, selectedLocations]);

  // Paginate filtered users
  const pagedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    console.log("123er",filteredUsers.slice(start, start + pageSize));
    
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const total = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Filter toggle functions
  const toggleRoleFilter = (role: 'Admin' | 'User') => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
    setPage(1);
  };

  const toggleDepartmentFilter = (dept: string) => {
    setSelectedDepartments(prev =>
      prev.includes(dept)
        ? prev.filter(d => d !== dept)
        : [...prev, dept]
    );
    setPage(1);
  };

  const toggleLocationFilter = (loc: string) => {
    setSelectedLocations(prev =>
      prev.includes(loc)
        ? prev.filter(l => l !== loc)
        : [...prev, loc]
    );
    setPage(1);
  };

  // Selection functions
  const allOnPageSelected = useMemo(() => 
    pagedUsers.length > 0 && pagedUsers.every(u => selectedIds.has(u.id)), 
    [pagedUsers, selectedIds]
  );

  const toggleSelectAllOnPage = () => {
    const next = new Set(selectedIds);
    if (allOnPageSelected) {
      pagedUsers.forEach(u => next.delete(u.id));
    } else {
      pagedUsers.forEach(u => next.add(u.id));
    }
    setSelectedIds(next);
  };

  const toggleRow = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); 
    else next.add(id);
    setSelectedIds(next);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search users..."
            className={`w-full border rounded-lg px-10 py-2 focus:outline-none focus:ring-2 focus:ring-pink-500 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white placeholder-white/40' : 'focus:border-transparent'}`}
          />
          <Search className={`absolute left-3 top-2.5 ${darkMode ? 'text-white/50' : 'text-gray-400'}`} size={18} />
        </div>
        <div className="flex items-center gap-2">
          <Dropdown
            trigger={
              <button className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${darkMode ? 'border-white/10 text-white/80 hover:bg-white/10' : 'hover:bg-gray-50'}`}>
                <Filter size={16} /> Filters
              </button>
            }
            align="left"
            darkMode={darkMode}
          >
            <div className="p-2 w-64">
              <div className={`mb-2 text-xs font-semibold ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>Role</div>
              <div className="flex items-center gap-3 mb-3">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" className="sr-only" checked={selectedRoles.includes('Admin')} onChange={() => toggleRoleFilter('Admin')} />
                  <span className={`inline-block h-5 w-5 rounded relative ${selectedRoles.includes('Admin') ? 'bg-gradient-to-r from-red-600 to-pink-600 border-transparent' : `${darkMode ? 'border-white/20' : 'border-gray-300'} border`}`}>
                    <span className={`absolute inset-0 flex items-center justify-center text-white pointer-events-none ${selectedRoles.includes('Admin') ? 'opacity-100' : 'opacity-0'}`}>
                      <Check size={12} strokeWidth={3} className="text-white" />
                    </span>
                  </span>
                  <span className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-xs`}>Admin</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" className="sr-only" checked={selectedRoles.includes('User')} onChange={() => toggleRoleFilter('User')} />
                  <span className={`inline-block h-5 w-5 rounded relative ${selectedRoles.includes('User') ? 'bg-gradient-to-r from-red-600 to-pink-600 border-transparent' : `${darkMode ? 'border-white/20' : 'border-gray-300'} border`}`}>
                    <span className={`absolute inset-0 flex items-center justify-center text-white pointer-events-none ${selectedRoles.includes('User') ? 'opacity-100' : 'opacity-0'}`}>
                      <Check size={12} strokeWidth={3} className="text-white" />
                    </span>
                  </span>
                  <span className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-xs`}>User</span>
                </label>
              </div>

              {availableDepartments.length > 0 && (
                <>
                  <div className={`mb-2 text-xs font-semibold ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>Department</div>
                  <div className="grid grid-cols-2 gap-2 mb-3 max-h-32 overflow-auto pr-1">
                    {availableDepartments.map(dept => (
                      <label key={dept} className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" className="sr-only" checked={selectedDepartments.includes(dept)} onChange={() => toggleDepartmentFilter(dept)} />
                        <span className={`inline-block h-5 w-5 rounded relative ${selectedDepartments.includes(dept) ? 'bg-gradient-to-r from-red-600 to-pink-600 border-transparent' : `${darkMode ? 'border-white/20' : 'border-gray-300'} border`}`}>
                          <span className={`absolute inset-0 flex items-center justify-center text-white pointer-events-none ${selectedDepartments.includes(dept) ? 'opacity-100' : 'opacity-0'}`}>
                            <Check size={12} strokeWidth={3} className="text-white" />
                          </span>
                        </span>
                        <span className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-xs truncate`} title={dept}>{dept}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}

              {availableLocations.length > 0 && (
                <>
                  <div className={`mb-2 text-xs font-semibold ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>Location</div>
                  <div className="grid grid-cols-2 gap-2 max-h-32 overflow-auto pr-1">
                    {availableLocations.map(loc => (
                      <label key={loc} className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" className="sr-only" checked={selectedLocations.includes(loc)} onChange={() => toggleLocationFilter(loc)} />
                        <span className={`inline-block h-5 w-5 rounded relative ${selectedLocations.includes(loc) ? 'bg-gradient-to-r from-red-600 to-pink-600 border-transparent' : `${darkMode ? 'border-white/20' : 'border-gray-300'} border`}`}>
                          <span className={`absolute inset-0 flex items-center justify-center text-white pointer-events-none ${selectedLocations.includes(loc) ? 'opacity-100' : 'opacity-0'}`}>
                            <Check size={12} strokeWidth={3} className="text-white" />
                          </span>
                        </span>
                        <span className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-xs truncate`} title={loc}>{loc}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Dropdown>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className={`border rounded-lg px-3 py-2 w-32 sm:w-40 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : ''}`}
          >
            {pageSizeOptions.map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <button
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${selectedIds.size === 0 ? 'opacity-50 cursor-not-allowed' : ''} ${darkMode ? (selectedIds.size === 0 ? 'border-white/10 text-white/50' : 'hover:bg-red-900/20 text-red-300 border-red-900/30') : 'hover:bg-red-50 text-red-600 border-red-200'}`}
            onClick={() => onDeleteSelected(Array.from(selectedIds))}
            disabled={selectedIds.size === 0}
            title="Delete selected"
          >
            <Trash2 size={18} /> Delete
          </button>
        </div>
      </div>

      <div className={`overflow-x-auto rounded-xl border ${darkMode ? 'border-white/10' : ''}`}>
        <table className={`min-w-full divide-y ${darkMode ? 'divide-white/10 text-white' : 'divide-gray-200 text-gray-900'} text-xs`}>
          <thead className={`${darkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
            <tr>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'}`}>
                <label className="inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAllOnPage} className="sr-only" />
                  <span className={`inline-block h-5 w-5 rounded relative ${allOnPageSelected ? 'bg-gradient-to-r from-red-600 to-pink-600 border-transparent' : `${darkMode ? 'border-white/20' : 'border-gray-300'} border`}`}>
                    <span className={`absolute inset-0 flex items-center justify-center text-white pointer-events-none ${allOnPageSelected ? 'opacity-100' : 'opacity-0'}`}>
                      <Check size={12} strokeWidth={3} className="text-white" />
                    </span>
                  </span>
                </label>
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Name</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Email</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Phone Number</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Department</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Position</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Location</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Role</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Created Date</th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-white/60' : 'text-gray-500'} whitespace-nowrap`}>Last login</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className={`${darkMode ? 'bg-[#0f1420] divide-white/10' : 'bg-white divide-gray-200'}`}>
            {pagedUsers.map(u => (
              <tr key={u.id} className={`${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleRow(u.id)} className="sr-only" />
                    <span className={`inline-block h-5 w-5 rounded relative ${selectedIds.has(u.id) ? 'bg-gradient-to-r from-red-600 to-pink-600 border-transparent' : `${darkMode ? 'border-white/20' : 'border-gray-300'} border`}`}>
                      <span className={`absolute inset-0 flex items-center justify-center text-white pointer-events-none ${selectedIds.has(u.id) ? 'opacity-100' : 'opacity-0'}`}>
                        <Check size={12} strokeWidth={3} className="text-white" />
                      </span>
                    </span>
                  </label>
                </td>
                <td className="px-4 py-3 whitespace-nowrap min-w-[280px]">
                  <div className="flex items-center gap-3">
                    {u.avatarUrl ? (
                      <img src={u.avatarUrl} alt={`${u.firstName} ${u.lastName}`} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 bg-blue-600 rounded-full text-white flex items-center justify-center text-xs font-semibold">
                        {u.firstName[0] || '?'}{u.lastName[0] || ''}
                      </div>
                    )}
                    <div>
                      <div className={`${darkMode ? 'text-white' : 'text-gray-900'} font-medium`}>{u.firstName} {u.lastName}</div>
                    </div>
                  </div>
                </td>
                <td className={`px-4 py-3 whitespace-nowrap min-w-[350px] ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>{u.email}</td>
                <td className={`px-4 py-3 whitespace-nowrap min-w-[150px] ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>{u.phoneNumber || '—'}</td>
                <td className={`px-4 py-3 whitespace-nowrap min-w-[120px] ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>{u.department || '—'}</td>
                <td className={`px-4 py-3 whitespace-nowrap min-w-[120px] ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>{u.position || '—'}</td>
                <td className={`px-4 py-3 whitespace-nowrap min-w-[120px] ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>{u.location || '—'}</td>
                <td className={`px-4 py-3 whitespace-nowrap min-w-[80px] ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>{u.role}</td>
                <td className={`px-4 py-3 whitespace-nowrap min-w-[150px] ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>{new Date(u.createdAt).toLocaleDateString()}</td>
                <td className={`px-4 py-3 whitespace-nowrap min-w-[200px] ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    className={`inline-flex items-center justify-center p-2 rounded-lg border ${darkMode ? 'border-white/10 text-white/80 hover:bg-white/10' : 'hover:bg-gray-50'}`}
                    onClick={() => onEdit(u)}
                    aria-label="Edit user"
                    title="Edit user"
                  >
                    <Edit3 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {pagedUsers.length === 0 && (
              <tr>
                <td colSpan={11} className={`px-4 py-10 text-center ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <div className={`text-xs ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>
          Showing {(total === 0 ? 0 : (page - 1) * pageSize + 1)}-
          {Math.min(page * pageSize, total)} of {total}
        </div>
        <div className="flex items-center gap-2">
          <button
            className={`px-2 py-1 border rounded-lg ${darkMode ? 'border-white/10 text-white/80 hover:bg-white/10' : 'hover:bg-gray-50'} disabled:opacity-50`}
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft size={18} />
          </button>
          <div className={`text-xs ${darkMode ? 'text-white/70' : ''}`}>Page {page} of {totalPages}</div>
          <button
            className={`px-2 py-1 border rounded-lg ${darkMode ? 'border-white/10 text-white/80 hover:bg-white/10' : 'hover:bg-gray-50'} disabled:opacity-50`}
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserTable;